import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ACTOR_KIND_OPTIONS,
  PLATFORM_OPTIONS,
  type ActorKind,
  type AnalyticsParams,
  type CounterType,
  type FeatureDetailParams,
  type Platform,
} from "../contract.js";
import { daysAgo, dayString, formatDay } from "../lib/format.js";
import { useScope } from "../lib/scope.js";
import {
  useActiveUsersQuery,
  useAdoptionQuery,
  useFeatureDetailQuery,
  useFeaturesQuery,
  useListAppsQuery,
  useOverviewQuery,
} from "../store/api.js";
import { Button } from "@/components/ui/button";

const TYPE_LABEL: Record<CounterType, string> = {
  feature: "Menu",
  screen: "Layar",
};

const TYPE_FILTERS = [
  { value: "all", label: "Semua" },
  { value: "feature", label: "Menu & aksi" },
  { value: "screen", label: "Layar" },
] as const;

type TypeFilter = (typeof TYPE_FILTERS)[number]["value"];

/** Columns the menu table can be ordered by; every one of them is a number. */
type SortColumn = "total" | "users" | "activeDays";

const SORT_LABEL: Record<SortColumn, string> = {
  total: "Total akses",
  users: "Pegawai",
  activeDays: "Hari aktif",
};

const ACTOR_LABEL: Record<ActorKind, string> = {
  device: "Perangkat (instalasi)",
  user: "Pengguna (akun)",
};

const ACTOR_BASIS: Record<ActorKind, string> = {
  device: "per perangkat",
  user: "per pengguna",
};

const PLATFORM_LABEL: Record<Platform, string> = {
  android: "Android",
  ios: "iOS",
};

export function AnalyticsPage() {
  const scope = useScope();
  const platform = scope.get("platform") as Platform | "";
  const actor: ActorKind = scope.get("actor") === "user" ? "user" : "device";

  const apps = useListAppsQuery();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(dayString(new Date()));

  useEffect(() => {
    if (scope.app || !apps.data || apps.data.length === 0) return;
    scope.set({ app: apps.data[0].slug }, { replace: true });
  }, [apps.data, scope]);

  const channels = apps.data?.find((entry) => entry.slug === scope.app)?.channels ?? [];

  const params: AnalyticsParams = {
    app: scope.app,
    channel: scope.channel || undefined,
    platform: platform || undefined,
    from,
    to,
    actor,
  };
  const skip = !scope.app;

  const activeUsers = useActiveUsersQuery(params, { skip });
  const features = useFeaturesQuery({ ...params, limit: 100 }, { skip });
  const adoption = useAdoptionQuery(params, { skip });
  const overview = useOverviewQuery(
    { app: scope.app, channel: scope.channel || undefined, actor },
    { skip }
  );

  const stickinessSeries = useMemo(
    () =>
      (activeUsers.data?.points ?? []).map((point) => ({
        day: point.day,
        stickiness: Math.round(point.stickiness * 1000) / 10,
      })),
    [activeUsers.data]
  );

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [sortColumn, setSortColumn] = useState<SortColumn>("total");
  const [selected, setSelected] = useState<{ type: CounterType; key: string } | null>(null);

  // The whole table is filtered and ordered in the browser: the range query
  // already returned every key, and re-fetching on each keystroke would make
  // typing feel slower than it looks.
  const menuRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (features.data ?? [])
      .filter((row) => typeFilter === "all" || row.type === typeFilter)
      .filter((row) => needle === "" || row.key.includes(needle))
      .slice()
      .sort((left, right) => {
        const delta = right[sortColumn] - left[sortColumn];
        return delta !== 0 ? delta : left.key.localeCompare(right.key);
      });
  }, [features.data, search, typeFilter, sortColumn]);

  const menuTotals = useMemo(
    () => ({
      events: menuRows.reduce((sum, row) => sum + row.total, 0),
      peakUsers: menuRows.reduce((peak, row) => (row.users > peak ? row.users : peak), 0),
    }),
    [menuRows]
  );

  const overviewStickiness =
    overview.data && overview.data.mau > 0 ? overview.data.dau / overview.data.mau : 0;

  const shownActor = overview.data?.actor ?? actor;
  const basis = ACTOR_BASIS[shownActor];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#101828]">Analitik Penggunaan</h1>
        <p className="text-[13.5px] text-[#667085]">
          Metrik keaktifan harian (DAU), mingguan (WAU), bulanan (MAU), serta adopsi versi aplikasi Digibikes.
        </p>
      </div>

      {/* Filter Row */}
      <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-[#EAECF0] bg-white p-3.5 shadow-xs text-xs">
        {/* App */}
        <select
          value={scope.app}
          onChange={(e) => scope.set({ app: e.target.value, channel: "" })}
          className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
        >
          {apps.data?.map((a) => (
            <option key={a.id} value={a.slug}>
              {a.name}
            </option>
          ))}
        </select>

        {/* Channel */}
        <select
          value={scope.channel}
          onChange={(e) => scope.set({ channel: e.target.value })}
          className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
        >
          <option value="">Semua kanal</option>
          {channels.map((ch) => (
            <option key={ch.id} value={ch.name}>
              {ch.name}
            </option>
          ))}
        </select>

        {/* Platform */}
        <select
          value={platform}
          onChange={(e) => scope.set({ platform: e.target.value })}
          className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
        >
          <option value="">Semua platform</option>
          {PLATFORM_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {PLATFORM_LABEL[p]}
            </option>
          ))}
        </select>

        {/* Basis */}
        <select
          value={actor}
          onChange={(e) => scope.set({ actor: e.target.value === "device" ? undefined : e.target.value })}
          className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
        >
          {ACTOR_KIND_OPTIONS.map((k) => (
            <option key={k} value={k}>
              {ACTOR_LABEL[k]}
            </option>
          ))}
        </select>

        {/* Date From & To */}
        <div className="flex items-center gap-1.5 ml-auto">
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2 text-xs text-[#344054]"
          />
          <span className="text-[#98A2B3]">s.d</span>
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2 text-xs text-[#344054]"
          />
        </div>
      </div>

      {/* 5 Metric Stat Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">DAU (Hari Ini)</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {overview.data?.dau.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">{basis}</span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">WAU (7 Hari)</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {overview.data?.wau.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">{basis}</span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">MAU (30 Hari)</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {overview.data?.mau.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">{basis}</span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Stickiness (DAU/MAU)</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#2563EB] font-mono">
            {overview.data && overview.data.mau > 0
              ? `${(overviewStickiness * 100).toFixed(1)}%`
              : "0.0%"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Retensi harian</span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Total Terdaftar</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {overview.data?.devices.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Perangkat terpasang</span>
        </div>
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Trend Pengguna Aktif */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
          <h3 className="text-sm font-bold text-[#101828]">Tren Keaktifan (DAU, WAU, MAU)</h3>
          <p className="text-xs text-[#667085]">Fluktuasi pengguna aktif sepanjang rentang tanggal</p>
          <div className="mt-4 h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={activeUsers.data?.points ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis dataKey="day" stroke="#98A2B3" fontSize={11} />
                <YAxis stroke="#98A2B3" fontSize={11} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="dau" name="DAU" stroke="#2563EB" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="wau" name="WAU" stroke="#17B26A" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="mau" name="MAU" stroke="#F79009" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Stickiness Ratio */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
          <h3 className="text-sm font-bold text-[#101828]">Rasio Stickiness (DAU / MAU %)</h3>
          <p className="text-xs text-[#667085]">Indikator keterikatan pengguna pada aplikasi</p>
          <div className="mt-4 h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stickinessSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis dataKey="day" stroke="#98A2B3" fontSize={11} />
                <YAxis stroke="#98A2B3" fontSize={11} domain={[0, 100]} />
                <Tooltip />
                <Line type="monotone" dataKey="stickiness" name="Stickiness %" stroke="#2563EB" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Menu usage: every key, not a top ten, and every row opens a detail. */}
      <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs sm:p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-[#101828]">Penggunaan Menu</h3>
            <p className="text-xs text-[#667085]">
              {menuRows.length.toLocaleString("id-ID")} menu tercatat ·{" "}
              {menuTotals.events.toLocaleString("id-ID")} total akses · puncak{" "}
              {menuTotals.peakUsers.toLocaleString("id-ID")} pegawai pada satu menu. Klik satu
              baris untuk melihat siapa saja yang mengaksesnya.
            </p>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari menu…"
              className="h-8.5 w-full min-w-0 flex-1 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none sm:w-52 sm:flex-none"
            />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
            >
              {TYPE_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Eight columns cannot shrink into a phone; the table scrolls instead
            of wrapping each cell into an unreadable stack. */}
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] text-[#667085]">
                <th className="pb-2">Nama</th>
                <th className="pb-2">Jenis</th>
                {(Object.keys(SORT_LABEL) as SortColumn[]).map((column) => (
                  <th key={column} className="pb-2 text-right">
                    <button
                      type="button"
                      onClick={() => setSortColumn(column)}
                      className={
                        sortColumn === column
                          ? "font-semibold text-[#2563EB]"
                          : "hover:text-[#344054]"
                      }
                    >
                      {SORT_LABEL[column]}
                      {sortColumn === column ? " ↓" : ""}
                    </button>
                  </th>
                ))}
                <th className="pb-2 text-right">Rata-rata/pegawai</th>
                <th className="pb-2 text-right">Terakhir</th>
                {/* Share is computed within a type: a menu's weight among menus
                    is a real number, a menu's weight among screens is not. */}
                <th className="pb-2 text-right">Porsi (per jenis)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F6F7F9]">
              {menuRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-4 text-center text-[#98A2B3]">
                    {features.isLoading ? "Memuat…" : "Belum ada data penggunaan menu."}
                  </td>
                </tr>
              ) : (
                menuRows.map((row) => (
                  <tr
                    key={`${row.type}|${row.key}`}
                    onClick={() => setSelected({ type: row.type, key: row.key })}
                    className="cursor-pointer hover:bg-[#F9FAFB]"
                  >
                    <td className="py-2 font-mono text-[#101828]">{row.key}</td>
                    <td className="py-2">
                      <span className="rounded-md bg-[#F2F4F7] px-1.5 py-0.5 text-[11px] text-[#475467]">
                        {TYPE_LABEL[row.type]}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono font-medium text-[#2563EB]">
                      {row.total.toLocaleString("id-ID")}
                    </td>
                    <td className="py-2 text-right font-mono text-[#101828]">
                      {row.users.toLocaleString("id-ID")}
                    </td>
                    <td className="py-2 text-right font-mono text-[#667085]">
                      {row.activeDays.toLocaleString("id-ID")}
                    </td>
                    <td className="py-2 text-right font-mono text-[#667085]">
                      {row.users === 0 ? "—" : (row.total / row.users).toFixed(1)}
                    </td>
                    <td className="py-2 text-right text-[#667085]">{formatDay(row.lastDay)}</td>
                    <td className="py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-[#F2F4F7]">
                          <div
                            className="h-full rounded-full bg-[#2563EB]"
                            style={{ width: `${Math.round(row.share * 100)}%` }}
                          />
                        </div>
                        <span className="w-10 font-mono text-[#667085]">
                          {(row.share * 100).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected ? (
        <FeatureDetailModal
          params={{ ...params, ...selected }}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}

interface FeatureDetailModalProps {
  params: FeatureDetailParams;
  onClose: () => void;
}

/**
 * Mounted only while a row is open, which is what keeps the drill-down query
 * from running for every key in the table.
 */
function FeatureDetailModal({ params, onClose }: FeatureDetailModalProps) {
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");

  // A menu used by the whole fleet would otherwise fire a query per keystroke
  // against a table with a row per employee per day.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const detail = useFeatureDetailQuery({ ...params, q: term || undefined });
  const data = detail.data;

  // `small` is for the one card that holds a date range instead of a count: at
  // the numeric size it wraps to three lines and breaks the row's rhythm.
  const stats: Array<{ label: string; value: string; small?: boolean }> = data
    ? [
        { label: "Total akses", value: data.total.toLocaleString("id-ID") },
        { label: "Pegawai unik", value: data.users.toLocaleString("id-ID") },
        { label: "Hari aktif", value: data.activeDays.toLocaleString("id-ID") },
        {
          label: "Rata-rata/pegawai",
          value: data.users === 0 ? "—" : (data.total / data.users).toFixed(1),
        },
        {
          label: "Rentang",
          value:
            data.firstDay === null
              ? "—"
              : `${formatDay(data.firstDay)} — ${formatDay(data.lastDay)}`,
          small: true,
        },
      ]
    : [];

  // The overlay is built the same way the other pages build theirs. The stats
  // grid is five cards, so it lays out 2 / 3 / 5 across the breakpoints.
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 backdrop-blur-xs sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border border-[#EAECF0] bg-white p-4 shadow-xl sm:max-h-[90vh] sm:rounded-2xl sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-bold text-[#101828]">
              {TYPE_LABEL[params.type]} · <span className="font-mono">{params.key}</span>
            </h3>
            <p className="text-xs text-[#667085]">
              {formatDay(params.from)} — {formatDay(params.to)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup"
            className="rounded-lg px-2 py-1 text-lg leading-none text-[#667085] hover:bg-[#F2F4F7]"
          >
            ×
          </button>
        </div>

        {detail.isLoading ? (
          <p className="mt-4 text-xs text-[#667085]">Memuat…</p>
        ) : !data ? (
          <p className="mt-4 text-xs text-[#667085]">Detail menu gagal dimuat.</p>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-[#EAECF0] p-3">
                  <span className="text-[11px] text-[#667085]">{stat.label}</span>
                  <div
                    className={
                      stat.small
                        ? "mt-1 text-xs font-semibold text-[#101828]"
                        : "mt-1 font-mono text-base font-bold text-[#101828]"
                    }
                  >
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <h4 className="text-xs font-bold text-[#101828]">Akses per hari</h4>
              <div className="mt-2 h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.perDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                    <XAxis dataKey="day" stroke="#98A2B3" fontSize={11} />
                    <YAxis stroke="#98A2B3" fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total" name="Akses" fill="#2563EB" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="users" name="Pegawai" fill="#17B26A" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h4 className="text-xs font-bold text-[#101828]">Siapa yang mengakses</h4>
                  <p className="text-[11px] text-[#667085]">
                    Diurutkan dari yang paling sering membuka menu ini
                    {data.matched > data.actors.length
                      ? ` · menampilkan ${data.actors.length.toLocaleString("id-ID")} dari ${data.matched.toLocaleString("id-ID")} pegawai`
                      : ""}
                    .
                  </p>
                </div>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Cari NIP atau nama…"
                  className="h-8 w-full rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none sm:w-56"
                />
              </div>
              <div className="mt-2 max-h-[280px] overflow-auto">
                <table className="w-full min-w-[560px] text-left text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-[#EAECF0] text-[#667085]">
                      <th className="pb-2">NIP</th>
                      <th className="pb-2">Nama</th>
                      <th className="pb-2 text-right">Akses</th>
                      <th className="pb-2 text-right">Hari aktif</th>
                      <th className="pb-2 text-right">Pertama</th>
                      <th className="pb-2 text-right">Terakhir</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F6F7F9]">
                    {data.actors.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-[#98A2B3]">
                          {term
                            ? `Tidak ada pegawai yang cocok dengan "${term}".`
                            : "Belum ada akses pada rentang ini."}
                        </td>
                      </tr>
                    ) : (
                      data.actors.map((actorRow) => (
                        <tr key={actorRow.nip}>
                          <td className="py-2 font-mono text-[#101828]">{actorRow.nip}</td>
                          <td className="py-2 text-[#101828]">{actorRow.name}</td>
                          <td className="py-2 text-right font-mono font-medium text-[#2563EB]">
                            {actorRow.total.toLocaleString("id-ID")}
                          </td>
                          <td className="py-2 text-right font-mono text-[#667085]">
                            {actorRow.activeDays.toLocaleString("id-ID")}
                          </td>
                          <td className="py-2 text-right text-[#667085]">
                            {formatDay(actorRow.firstDay)}
                          </td>
                          <td className="py-2 text-right text-[#667085]">
                            {formatDay(actorRow.lastDay)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
